import PropTypes from 'prop-types';
import React, { PureComponent, Fragment } from 'react';
import { Motion, spring } from 'react-motion';
import FocusTrap from 'focus-trap-react';
import styled, { css, createGlobalStyle } from 'styled-components';
import GreyCloseIcon from '../../images/ic-close-grey.svg';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';

import Portal from '@g4/boneyard/lib/Portal';
import { ScrollProvider } from '@g4/boneyard/lib/Scroll';
import hooks, { CLOSE_DISMISSIBLE_BUTTON } from '@g4/automation-hooks';

import Text from '../Boneyard/Text';
import { displayName as dismissibleButtonBarDisplayName } from './DismissibleButtonBar';
import blueCloseIcon from '../../images/ic-close-blue.svg';
import whiteCloseIcon from '../../images/ic-close-white.svg';

export const CLASSNAME_DISMISSIBLE_ACTIVE = 'dismissible-active';

export const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  z-index: 999;
  overflow-x: auto;
  padding: 20px;
  ${props =>
    props.noPadding &&
    css`
      padding: unset;
    `}

  ${({ theme, themeColor }) => {
    switch (themeColor) {
      case 'blue':
        return css`
          background-image: linear-gradient(
            to top,
            ${theme.colors.hob.blueSearchWidget.backgroundFrom},
            ${theme.colors.hob.blueSearchWidget.backgroundTo}
          );
        `;
      case 'grey':
        return css`
          background-image: linear-gradient(
            to top,
            ${theme.colors.hob.greySearchWidget.backgroundFrom},
            ${theme.colors.hob.greySearchWidget.backgroundTo}
          );
        `;
      default:
        return css`
          background-color: #fff;
        `;
    }
  }}

  /* GMOB2-16: prevent scolling beyond bounds on iOS */
  overflow-y: hidden;

  /* GMOB-249: momentum scrolling on iOS */
  -webkit-overflow-scrolling: touch;
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 15px;
  top: 30px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: stretch;
  width: 50px;
  border: 0;
  padding: 0;
  margin: 0;
  background: none;
`;

export const CloseButtonContent = styled.span`
  position: relative;
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    margin: auto;
    height: calc(100% - 20px);
    width: 1px;
  }
`;

// Since we're handling overflow here, `scrollTop` is no longer effective on the document.
export const Content = styled(ScrollProvider)`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;

  /* Prevents ButtonBar overlapping content when there is little screen real-estate (GMOB-400) */
  min-height: 100px;
`;

export const TitleBar = styled.div`
  display: flex;
  justify-content: center;
  height: 50px;
`;

export const Title = styled.h1`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  color: ${props => props.theme.colors.button.darkText};
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/* GMOB-901: Hide content to prevent iOS navigation to fields outside of the Dismissible */
// eslint-disable-next-line no-unused-expressions
export const DismissableStyles = createGlobalStyle`
  #root.dismissible-active {
    visibility: hidden;
  }
`;

export class Dismissible extends PureComponent {
  constructor(props) {
    super();

    this.state = {
      isWorking: false,
      // Allows us to return focus to where it was before transitioning to the Dismissible
      originalFocus: null,
    };

    this.toggleIsWorking = this.toggleIsWorking.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.rootElement = document.getElementById('root');

    this.content = this.extractContent(props.children);
  }

  static propTypes = {
    toRef: PropTypes.func,
    themeColor: PropTypes.oneOf(['white', 'blue', 'grey']),
    /** Title to display above the content of the component */
    title: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    /** Determines if the component is currently open */
    active: PropTypes.bool.isRequired,
    /**
      Determines which direction the component will go in as it overlays the
      parent page in a transition animation (it will transition in
      the opposite direction as it closes)
    */
    direction: PropTypes.oneOf(['left', 'up']),
    onDismiss: PropTypes.func,
    onScroll: PropTypes.func,
    children: PropTypes.node,
    className: PropTypes.string,
    noPadding: PropTypes.bool,
    isSunseekerPromotion: PropTypes.bool,
    isSunseekerPromotionCard: PropTypes.bool,
  };

  static defaultProps = {
    noPadding: false,
    toRef: Function.prototype,
    onScroll: Function.prototype,
    direction: 'left',
    onDismiss: Function.prototype,
    theme: 'white',
  };

  static instancesOpen = 0;

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.content = this.extractContent(nextProps.children);
    if (nextProps.active !== this.props.active) {
      const nextState = {
        isWorking: true,
      };

      if (nextProps.active) {
        nextState.originalFocus = document.activeElement;
        disableBodyScroll(this.rootElement);
      } else {
        if (this.state.originalFocus) {
          this.state.originalFocus.focus();
        }
        /* istanbul ignore next */
        // we can not remove CLASSNAME_DISMISSIBLE_ACTIVE because it's only added when the animation has ended
        // and it will throw an error
        if (this.rootElement) {
          // (GMOB-901) Hide content to prevent iOS navigation to fields outside of the Dismissible
          this.constructor.instancesOpen = Math.max(
            this.constructor.instancesOpen - 1,
            0
          );

          if (!this.constructor.instancesOpen) {
            this.rootElement.classList.remove(CLASSNAME_DISMISSIBLE_ACTIVE);
          }
        }
        enableBodyScroll(this.rootElement);
      }

      this.setState(nextState);
    }
  }
  /* istanbul ignore next */
  // we can not remove CLASSNAME_DISMISSIBLE_ACTIVE because it's only added when the animation has ended
  // and it will throw an error
  componentWillUnmount() {
    if (this.rootElement) {
      // (GMOB-901) Hide content to prevent iOS navigation to fields outside of the Dismissible
      this.constructor.instancesOpen = Math.max(
        this.constructor.instancesOpen - 1,
        0
      );

      if (!this.constructor.instancesOpen) {
        this.rootElement.classList.remove(CLASSNAME_DISMISSIBLE_ACTIVE);
      }
    }
    clearAllBodyScrollLocks();
  }

  extractContent(children) {
    const list = React.Children.toArray(children);
    return list.filter(
      child =>
        child && child.type.displayName !== dismissibleButtonBarDisplayName
    );
  }

  extractButtonBar(children) {
    const list = React.Children.toArray(children);
    return list.filter(
      child =>
        child && child.type.displayName === dismissibleButtonBarDisplayName
    );
  }

  getCloseIcon = (
    themeColor,
    isSunseekerPromotion,
    isSunseekerPromotionCard
  ) => {
    if (isSunseekerPromotion || isSunseekerPromotionCard) {
      return GreyCloseIcon;
    } else if (themeColor === 'blue') {
      return whiteCloseIcon;
    } else {
      return blueCloseIcon;
    }
  };

  renderDismiss(isSunseekerPromotion, isSunseekerPromotionCard) {
    /* if the state is not visible, we should render a spinner/disable this button */
    const disabled = this.props.active ? {} : { disabled: 'disabled' };
    const { themeColor } = this.props;

    return (
      <CloseButton
        {...disabled}
        {...hooks.hook(CLOSE_DISMISSIBLE_BUTTON)}
        type="button"
        onClick={this.props.onDismiss}
      >
        <CloseButtonContent>
          <img
            src={this.getCloseIcon(
              themeColor,
              isSunseekerPromotion,
              isSunseekerPromotionCard
            )}
            alt="Close"
          />
        </CloseButtonContent>
      </CloseButton>
    );
  }

  renderContent(style) {
    const buttonBar = this.extractButtonBar(this.props.children);

    let {
      direction,
      isSunseekerPromotion,
      isSunseekerPromotionCard,
    } = this.props;

    if (this.props.active || this.state.isWorking) {
      let animateTo = {};

      if (direction === 'up') {
        animateTo.top = `${style.offset}%`;
      } else {
        animateTo.left = `${style.offset}%`;
      }

      return (
        <Root
          themeColor={this.props.themeColor}
          style={animateTo}
          noPadding={this.props.noPadding}
        >
          <FocusTrap
            active={true}
            // Avoids the default behaviour of preventing event bubbling (GMOB-441)
            focusTrapOptions={{
              clickOutsideDeactivates: true,
            }}
          >
            <ContentWrapper onKeyDown={this.handleKeyDown}>
              <TitleBar>
                <Title>
                  <Text weight={500} size={16}>
                    {this.props.title}
                  </Text>
                </Title>
                {this.renderDismiss(
                  isSunseekerPromotion,
                  isSunseekerPromotionCard
                )}
              </TitleBar>
              <PureWrap toRef={this.props.toRef} onScroll={this.props.onScroll}>
                {this.content}
              </PureWrap>
              {buttonBar}
            </ContentWrapper>
          </FocusTrap>
        </Root>
      );
    }

    return null;
  }

  /* istanbul ignore next */
  // can't test this function as it's called when the animation has ended
  toggleIsWorking() {
    this.setState({
      isWorking: !this.state.isWorking,
    });

    if (this.rootElement) {
      // (GMOB-901) Hide content to prevent iOS navigation to fields outside of the Dismissible
      if (this.props.active) {
        this.constructor.instancesOpen++;
        this.rootElement.classList.add(CLASSNAME_DISMISSIBLE_ACTIVE);
      }
    }
  }

  handleKeyDown(e) {
    if (e.keyCode === 27) {
      this.props.onDismiss(e);
    }
  }

  render() {
    return (
      <Fragment>
        <Motion
          style={{
            offset: spring(this.props.active ? 0 : 100, {
              stiffness: 150,
              damping: 26,
              precision: 0.1,
            }),
          }}
          onRest={this.toggleIsWorking}
        >
          {style => {
            return (
              <Portal>
                <div className={this.props.className}>
                  {this.renderContent(style)}
                </div>
              </Portal>
            );
          }}
        </Motion>
      </Fragment>
    );
  }
}

// Intentionally exported after Dismissible to avoid styleguidist not referencing the
// correct component
export class PureWrap extends PureComponent {
  static propTypes = {
    children: PropTypes.node,
    toRef: PropTypes.func,
    onScroll: PropTypes.func,
  };

  static defaultProps = {
    toRef: Function.prototype,
    onScroll: Function.prototype,
  };

  render() {
    return (
      <Content ref={this.props.toRef} onScroll={this.props.onScroll}>
        {this.props.children}
      </Content>
    );
  }
}

export default styled(Dismissible)``;
